// 🌟 MACHINE À POSITIVER - 100+ messages personnalisés
// Messages ultra-personnalisés basés sur le prénom et le genre

// Liste de prénoms courants en France pour détection intelligente
const COMMON_FIRST_NAMES = [
  // Prénoms féminins
  'marie', 'jeanne', 'marguerite', 'françoise', 'suzanne', 'louise', 'anne', 'madeleine',
  'catherine', 'jacqueline', 'denise', 'germaine', 'yvonne', 'renée', 'simone', 'andrée',
  'nathalie', 'isabelle', 'sylvie', 'christine', 'martine', 'monique', 'nicole', 'patricia',
  'véronique', 'sandrine', 'céline', 'valérie', 'stéphanie', 'karine', 'sophie', 'laurence',
  'corinne', 'pascale', 'emmanuelle', 'delphine', 'florence', 'audrey', 'aurélie', 'émilie',
  'julie', 'laura', 'marion', 'camille', 'léa', 'manon', 'chloé', 'sarah', 'océane', 'marine',
  'pauline', 'anaïs', 'lucie', 'clémence', 'clara', 'emma', 'inès', 'jade', 'léna', 'lola',
  'zoé', 'alice', 'juliette', 'charlotte', 'rose', 'ambre', 'anna', 'eva', 'lina', 'mila',
  'victoria', 'agathe', 'margot', 'romane', 'maëlys', 'lou', 'nina', 'lily', 'elena', 'luna',
  'louna', 'nour', 'yasmine', 'fatima', 'aïcha', 'khadija', 'samira', 'leïla', 'maria', 'rosa',
  'julia', 'sofia', 'carla', 'jessica', 'jennifer', 'melissa', 'vanessa', 'cindy', 'kelly',
  'sabrina', 'virginie', 'laetitia', 'aurore', 'morgane', 'élodie', 'mélanie', 'estelle',
  'gaëlle', 'magalie', 'priscilla', 'déborah', 'alexandra', 'amandine', 'charlène', 'maeva',
  'coralie', 'justine', 'mathilde', 'adeline', 'amélie', 'noémie', 'margaux', 'constance',
  // Prénoms masculins courants
  'jean', 'pierre', 'michel', 'andré', 'philippe', 'alain', 'bernard', 'jacques', 'daniel',
  'patrick', 'christian', 'claude', 'dominique', 'pascal', 'thierry', 'laurent', 'nicolas',
  'franck', 'eric', 'christophe', 'david', 'olivier', 'stéphane', 'frédéric', 'sébastien',
  'jérôme', 'vincent', 'guillaume', 'alexandre', 'julien', 'maxime', 'antoine', 'thomas',
  'kevin', 'jordan', 'dylan', 'lucas', 'hugo', 'mathis', 'léo', 'nathan', 'ethan', 'noah',
  'gabriel', 'louis', 'raphaël', 'arthur', 'jules', 'adam', 'maël', 'liam', 'paul', 'sacha',
  'mohamed', 'karim', 'mehdi', 'youssef', 'adam', 'amine', 'omar', 'ali', 'ibrahim', 'ahmed'
];

// Liste de noms de famille courants (pour distinguer prénom/nom)
const COMMON_LAST_NAMES = [
  'martin', 'bernard', 'thomas', 'petit', 'robert', 'richard', 'durand', 'dubois', 'moreau',
  'laurent', 'simon', 'michel', 'lefebvre', 'leroy', 'roux', 'david', 'bertrand', 'morel',
  'fournier', 'girard', 'bonnet', 'dupont', 'lambert', 'fontaine', 'rousseau', 'vincent',
  'muller', 'lefevre', 'faure', 'andre', 'mercier', 'blanc', 'guerin', 'boyer', 'garnier',
  'chevalier', 'francois', 'legrand', 'gauthier', 'garcia', 'perrin', 'robin', 'clement',
  'morin', 'nicolas', 'henry', 'roussel', 'mathieu', 'gautier', 'masson', 'marchand', 'duval',
  'denis', 'dumont', 'marie', 'lemaire', 'noel', 'meyer', 'dufour', 'meunier', 'brun', 'blanchard',
  'giraud', 'joly', 'riviere', 'lucas', 'brunet', 'gaillard', 'barbier', 'arnaud', 'martinez',
  'gerard', 'roche', 'renard', 'schmitt', 'roy', 'leroux', 'colin', 'vidal', 'caron', 'picard',
  'roger', 'fabre', 'aubert', 'lemoine', 'renaud', 'dumas', 'lacroix', 'olivier', 'philippe'
];

/**
 * 🧠 Extrait intelligemment le PRÉNOM d'un nom complet
 * Gère les cas : "Prénom Nom", "Nom Prénom", "Prénom", "NOM PRÉNOM"
 * 
 * @param {string} fullName - Le nom complet du client
 * @returns {string} - Le prénom extrait
 */
export function extractFirstName(fullName) {
  if (!fullName) return '';
  
  // Nettoyer et normaliser
  const cleanName = fullName.trim();
  const parts = cleanName.split(/\s+/).filter(p => p.length > 0);
  
  if (parts.length === 0) return '';
  if (parts.length === 1) return capitalizeFirstLetter(parts[0]);
  
  // Normaliser pour comparaison (sans accents, minuscules)
  const normalize = (str) => str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  
  // Stratégie 1 : Chercher un prénom connu
  for (const part of parts) {
    const normalizedPart = normalize(part);
    if (COMMON_FIRST_NAMES.some(name => normalize(name) === normalizedPart)) {
      return capitalizeFirstLetter(part);
    }
  }
  
  // Stratégie 2 : Si le premier mot est un nom de famille connu, prendre le second
  const firstNormalized = normalize(parts[0]);
  if (COMMON_LAST_NAMES.some(name => normalize(name) === firstNormalized)) {
    return capitalizeFirstLetter(parts[1]);
  }
  
  // Stratégie 3 : Si le premier mot est tout en majuscules (NOM), prendre le second
  if (parts[0] === parts[0].toUpperCase() && parts.length > 1) {
    return capitalizeFirstLetter(parts[1]);
  }
  
  // Stratégie 4 : Si le dernier mot est tout en majuscules, c'est probablement le nom
  if (parts[parts.length - 1] === parts[parts.length - 1].toUpperCase()) {
    return capitalizeFirstLetter(parts[0]);
  }
  
  // Par défaut : prendre le premier mot (cas le plus courant "Prénom Nom")
  return capitalizeFirstLetter(parts[0]);
}

/**
 * Met en majuscule la première lettre
 */
function capitalizeFirstLetter(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Liste de prénoms féminins courants en France pour détection automatique
const FEMININE_NAMES = [
  'marie', 'jeanne', 'marguerite', 'françoise', 'suzanne', 'louise', 'anne', 'madeleine',
  'catherine', 'jacqueline', 'denise', 'germaine', 'yvonne', 'renée', 'simone', 'andrée',
  'marcelle', 'paulette', 'georgette', 'odette', 'lucienne', 'henriette', 'raymonde', 'mireille',
  'nathalie', 'isabelle', 'sylvie', 'christine', 'martine', 'françoise', 'monique', 'nicole',
  'patricia', 'véronique', 'sandrine', 'céline', 'valérie', 'stéphanie', 'karine', 'sophie',
  'laurence', 'corinne', 'pascale', 'emmanuelle', 'delphine', 'florence', 'audrey', 'aurélie',
  'émilie', 'julie', 'marie', 'laura', 'marion', 'camille', 'léa', 'manon', 'chloé', 'sarah',
  'océane', 'marine', 'pauline', 'anaïs', 'lucie', 'clémence', 'clara', 'emma', 'inès', 'jade',
  'léna', 'lola', 'zoé', 'alice', 'juliette', 'charlotte', 'louise', 'rose', 'ambre', 'anna',
  'eva', 'lina', 'mila', 'victoria', 'agathe', 'margot', 'romane', 'jeanne', 'maëlys', 'lou',
  'nina', 'lily', 'elena', 'luna', 'louna', 'nour', 'yasmine', 'fatima', 'aïcha', 'khadija',
  'samira', 'leïla', 'sarah', 'maria', 'rosa', 'anna', 'julia', 'sofia', 'carla', 'elena',
  'jessica', 'jennifer', 'melissa', 'vanessa', 'cindy', 'kelly', 'kimberley', 'tiffany',
  'sabrina', 'virginie', 'laetitia', 'aurore', 'morgane', 'élodie', 'mélanie', 'estelle',
  'gaëlle', 'magalie', 'priscilla', 'déborah', 'alexandra', 'amandine', 'charlène', 'maeva',
  'coralie', 'justine', 'mathilde', 'adeline', 'amélie', 'noémie', 'margaux', 'constance'
];

/**
 * Détecte le genre probable basé sur le prénom
 * @param {string} firstName - Le prénom à analyser
 * @returns {'f' | 'm' | 'neutral'} - Le genre détecté
 */
export function detectGender(firstName) {
  if (!firstName) return 'neutral';
  
  const normalizedName = firstName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  
  // Vérifie si c'est un prénom féminin connu
  if (FEMININE_NAMES.some(name => {
    const normalizedFemName = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return normalizedFemName === normalizedName;
  })) {
    return 'f';
  }
  
  // Heuristique : prénoms finissant par 'a', 'e', 'ine', 'elle', 'ette' sont souvent féminins
  if (/[ae]$/.test(normalizedName) || /(ine|elle|ette|ise|ose|ine)$/.test(normalizedName)) {
    return 'f';
  }
  
  return 'm';
}

/**
 * Génère un message positif personnalisé
 * @param {string} firstName - Le prénom du client
 * @param {string} [genderOverride] - Genre forcé ('f' ou 'm')
 * @returns {{ greeting: string, subtext: string }} - Le message de bienvenue
 */
export function getPositiveMessage(firstName, genderOverride = null) {
  const gender = genderOverride || detectGender(firstName);
  const isFemale = gender === 'f';
  
  // Accords grammaticaux
  const e = isFemale ? 'e' : '';
  const belle = isFemale ? 'belle' : 'beau';
  const champion = isFemale ? 'championne' : 'champion';
  const pret = isFemale ? 'prête' : 'prêt';
  const content = isFemale ? 'contente' : 'content';
  const meilleur = isFemale ? 'meilleure' : 'meilleur';
  const seul = isFemale ? 'seule' : 'seul';
  const magnifique = 'magnifique'; // invariable
  const special = isFemale ? 'spéciale' : 'spécial';
  const prefere = isFemale ? 'préférée' : 'préféré';
  const rayonnant = isFemale ? 'rayonnante' : 'rayonnant';
  const superbe = 'superbe'; // invariable
  const attendu = isFemale ? 'attendue' : 'attendu';
  const adore = isFemale ? 'adorée' : 'adoré';
  const bienvenu = isFemale ? 'bienvenue' : 'bienvenu';
  const fier = isFemale ? 'fière' : 'fier';
  const premier = isFemale ? 'première' : 'premier';
  const cheri = isFemale ? 'chérie' : 'chéri';
  const unique = 'unique'; // invariable
  const genial = isFemale ? 'géniale' : 'génial';
  const incroyable = 'incroyable'; // invariable
  const formidable = 'formidable'; // invariable

  // 100+ MESSAGES POSITIFS ULTRA-PERSONNALISÉS
  const messages = [
    // 🌟 Messages enthousiastes sur le prénom
    { greeting: `Bonnjourrrr ${firstName} !`, subtext: `${firstName}, c'est juste un prénom ${magnifique} ! 💜` },
    { greeting: `Heyyy ${firstName} !`, subtext: `On adore quand t'es là, ${firstName} ! 🌟` },
    { greeting: `${firstName} ! 💜`, subtext: `Toi t'as un prénom qui claque, avoue ! ✨` },
    { greeting: `Coucouuu ${firstName} !`, subtext: `${firstName}... Ça sonne comme une mélodie ! 🎵` },
    { greeting: `Oh ${firstName} !`, subtext: `Sérieux, ${firstName} c'est trop stylé comme prénom ! 😎` },
    { greeting: `${firstName} est là !`, subtext: `Et bah dis donc, ${firstName} ça en jette ! 🔥` },
    { greeting: `Yooo ${firstName} !`, subtext: `${firstName}, un prénom de ${champion} ! 🏆` },
    { greeting: `Hello ${firstName} !`, subtext: `${firstName}... Classe, élégant${e}, parfait${e} ! 💫` },
    
    // ☀️ Messages sur la journée
    { greeting: `${belle.charAt(0).toUpperCase() + belle.slice(1)} journée ${firstName} !`, subtext: `Aujourd'hui va être ta journée, on le sent ! ☀️` },
    { greeting: `Salut ${firstName} !`, subtext: `Quelle ${belle} journée pour croiser ta route ! 🌈` },
    { greeting: `Hey ${firstName} !`, subtext: `Les étoiles sont alignées pour toi aujourd'hui ! ⭐` },
    { greeting: `Bonjour ${firstName} !`, subtext: `Aujourd'hui, que du bonheur pour toi ! 🎁` },
    { greeting: `Coucou ${firstName} !`, subtext: `C'est une super journée qui commence ! ☀️` },
    { greeting: `${firstName} ! ✨`, subtext: `Le soleil brille plus fort quand t'es là ! 🌞` },
    { greeting: `Ohhhh ${firstName} !`, subtext: `Journée parfaite pour se faire plaisir ! 🎉` },
    { greeting: `Waaah ${firstName} !`, subtext: `T'as senti ? C'est l'énergie positive de la journée ! ⚡` },
    
    // 🔥 Messages enthousiastes
    { greeting: `${firstName} dans la place !`, subtext: `Attention, la ${isFemale ? 'star' : 'star'} est arrivé${e} ! 🌟` },
    { greeting: `BOOM ${firstName} !`, subtext: `T'es ${pret}${e} à tout déchirer aujourd'hui ? 💪` },
    { greeting: `${firstName} ! 🔥`, subtext: `La légende est de retour ! 🏆` },
    { greeting: `Let's gooo ${firstName} !`, subtext: `Ensemble, on va faire des merveilles ! 🚀` },
    { greeting: `${firstName} !!! 🎉`, subtext: `La fête peut commencer maintenant ! 🥳` },
    { greeting: `Yeah ${firstName} !`, subtext: `T'es ${pret}${e} pour une journée de ouf ? 🔥` },
    { greeting: `Go ${firstName} go !`, subtext: `Rien ne peut t'arrêter aujourd'hui ! ⚡` },
    { greeting: `${firstName} power !`, subtext: `T'as une énergie de dingue ! 💥` },
    
    // 💜 Messages affectueux
    { greeting: `${firstName} ${cheri}${e} !`, subtext: `Tellement ${content}${e} de te voir ! 💜` },
    { greeting: `Mon ${prefere}${e} ${firstName} !`, subtext: `T'es vraiment quelqu'un de ${special} ! ✨` },
    { greeting: `Ah ${firstName} !`, subtext: `On est toujours ${content}${e}s quand tu passes ! 🥰` },
    { greeting: `${firstName} !`, subtext: `Tu nous avais manqué, tu sais ? 💝` },
    { greeting: `Re-coucou ${firstName} !`, subtext: `Toujours un plaisir de te revoir ! 😊` },
    { greeting: `${firstName} ❤️`, subtext: `T'es de la famille ici, oublie jamais ça ! 💜` },
    { greeting: `Notre ${firstName} !`, subtext: `Content${e}s que tu sois là ! 🤗` },
    { greeting: `Cher${e} ${firstName} !`, subtext: `T'es ${unique} et on t'adore ! 💫` },
    
    // 🏆 Messages valorisants
    { greeting: `${champion.charAt(0).toUpperCase() + champion.slice(1)} ${firstName} !`, subtext: `T'es vraiment ${formidable}, tu sais ça ? 🏆` },
    { greeting: `${firstName} le/la boss !`, subtext: `T'assures comme jamais ! 💪` },
    { greeting: `Super ${firstName} !`, subtext: `T'es ${incroyable}, ne change rien ! ⭐` },
    { greeting: `${firstName} ! 🌟`, subtext: `T'es ${meilleur}${e} de jour en jour ! 📈` },
    { greeting: `Bravo ${firstName} !`, subtext: `D'être toi, tout simplement ! 👏` },
    { greeting: `${firstName} !`, subtext: `T'as tout pour réussir, fonce ! 🚀` },
    { greeting: `King/Queen ${firstName} !`, subtext: `La royauté est dans la place ! 👑` },
    { greeting: `${firstName} ! 💎`, subtext: `T'es un${e} vrai${e} pépite ! ✨` },
    
    // 😄 Messages fun et décalés
    { greeting: `Yo yo yo ${firstName} !`, subtext: `Prêt${e} pour les bonnes vibes ? 🎶` },
    { greeting: `Toc toc ${firstName} !`, subtext: `Qui est là ? Le bonheur ! 🚪😄` },
    { greeting: `Pssst ${firstName} !`, subtext: `On a plein de surprises pour toi ! 🎁` },
    { greeting: `${firstName} !!!`, subtext: `Wow, t'es encore plus ${belle} qu'hier ! 😍` },
    { greeting: `Alerte ${firstName} !`, subtext: `Personne ${genial}${e} détecté${e} ! 🚨✨` },
    { greeting: `Breaking news !`, subtext: `${firstName} est là et c'est la meilleure nouvelle ! 📰` },
    { greeting: `Attention attention !`, subtext: `${firstName} arrive et ça va être ${genial} ! 📢` },
    { greeting: `${firstName} spotted !`, subtext: `Mission bonheur : activée ! 🎯` },
    
    // 🌈 Messages positifs et motivants
    { greeting: `${firstName} !`, subtext: `Tu mérites que des bonnes choses ! 🌈` },
    { greeting: `Hey ${firstName} !`, subtext: `Souris, t'es au bon endroit ! 😊` },
    { greeting: `Coucou ${firstName} !`, subtext: `La vie est belle et toi aussi ! 🌸` },
    { greeting: `Salut ${firstName} !`, subtext: `Aujourd'hui, on positive à fond ! ☀️` },
    { greeting: `${firstName} ! 🌟`, subtext: `T'es ${rayonnant}${e} aujourd'hui ! ✨` },
    { greeting: `Hello ${firstName} !`, subtext: `Le bonheur, c'est maintenant ! 🎉` },
    { greeting: `${firstName} !`, subtext: `Garde le smile, ça te va trop bien ! 😁` },
    { greeting: `Hé ${firstName} !`, subtext: `T'as le pouvoir de rendre cette journée géniale ! 💪` },
    
    // 🎁 Messages sur les avantages
    { greeting: `${firstName} VIP !`, subtext: `T'es traité${e} comme un${e} ${isFemale ? 'reine' : 'roi'} ici ! 👑` },
    { greeting: `${firstName} !`, subtext: `On a plein de pépites pour toi ! 🎁` },
    { greeting: `Lucky ${firstName} !`, subtext: `C'est ton jour de chance ! 🍀` },
    { greeting: `${firstName} !`, subtext: `Des surprises t'attendent ! 🎊` },
    { greeting: `Bienvenue ${firstName} !`, subtext: `Ton espace perso est prêt ! 🏠` },
    { greeting: `${firstName} ! 🎯`, subtext: `Tes avantages sont là pour toi ! 💝` },
    { greeting: `${firstName} !`, subtext: `On t'a gardé les meilleurs trucs ! 🌟` },
    { greeting: `Coucou ${firstName} !`, subtext: `C'est l'heure de se faire plaisir ! 🛍️` },
    
    // 💪 Messages de motivation
    { greeting: `Courage ${firstName} !`, subtext: `T'es capable de tout, on croit en toi ! 💪` },
    { greeting: `${firstName} !`, subtext: `Rien n'est impossible pour toi ! 🚀` },
    { greeting: `Allez ${firstName} !`, subtext: `Aujourd'hui, tu vas tout déchirer ! 🔥` },
    { greeting: `${firstName} !`, subtext: `Ta détermination est inspirante ! ⭐` },
    { greeting: `Fonce ${firstName} !`, subtext: `Le monde t'appartient ! 🌍` },
    { greeting: `${firstName} le/la warrior !`, subtext: `T'es invincible ! 🛡️` },
    { greeting: `${firstName} !`, subtext: `Continue comme ça, t'es sur la bonne voie ! 🎯` },
    { greeting: `Yes ${firstName} !`, subtext: `T'as tout compris à la vie ! 🧠✨` },
    
    // 🌸 Messages doux et chaleureux
    { greeting: `Douce journée ${firstName} !`, subtext: `Qu'elle soit remplie de bonheur ! 🌸` },
    { greeting: `${firstName} !`, subtext: `Prends soin de toi aujourd'hui ! 💆` },
    { greeting: `Cher${e} ${firstName} !`, subtext: `T'es précieux${e} à nos yeux ! 💎` },
    { greeting: `${firstName} !`, subtext: `On t'envoie plein d'ondes positives ! 🌊✨` },
    { greeting: `Tendre ${firstName} !`, subtext: `Le monde a besoin de gens comme toi ! 🌍💜` },
    { greeting: `${firstName} !`, subtext: `Ta bienveillance nous inspire ! 🙏` },
    { greeting: `${firstName} ! 🌷`, subtext: `T'es une belle personne, reste comme ça ! 💫` },
    { greeting: `${firstName} !`, subtext: `Ta présence réchauffe les cœurs ! ❤️‍🔥` },
    
    // 🎊 Messages festifs
    { greeting: `Fiesta ${firstName} !`, subtext: `C'est toujours la fête avec toi ! 🎊` },
    { greeting: `Party ${firstName} !`, subtext: `Let's goooo ! 🥳` },
    { greeting: `${firstName} !`, subtext: `On sort les confettis pour toi ! 🎉` },
    { greeting: `Célébrons ${firstName} !`, subtext: `Parce que t'es exceptionnel${e} ! 🍾` },
    { greeting: `${firstName} ! 🎈`, subtext: `Chaque jour avec toi est une fête ! 🎉` },
    { greeting: `Wooohoo ${firstName} !`, subtext: `La good vibe est là ! 🌈` },
    { greeting: `${firstName} !`, subtext: `On fête ton arrivée ! 🥂` },
    { greeting: `Hip hip ${firstName} !`, subtext: `Hourra ! T'es là ! 🙌` },
    
    // 🌟 Messages sur l'unicité
    { greeting: `${unique.charAt(0).toUpperCase() + unique.slice(1)} ${firstName} !`, subtext: `Y'a qu'un${e} ${firstName} comme toi ! 💜` },
    { greeting: `${firstName} !`, subtext: `T'es irremplaçable, tu sais ? 💎` },
    { greeting: `L'${unique} ${firstName} !`, subtext: `On a cassé le moule après toi ! 🏆` },
    { greeting: `${firstName} !`, subtext: `Ta personnalité est un cadeau ! 🎁` },
    { greeting: `${firstName} l'original${e} !`, subtext: `T'es une édition limitée ! ✨` },
    { greeting: `${firstName} !`, subtext: `Le monde serait fade sans toi ! 🌈` },
    { greeting: `Authentique ${firstName} !`, subtext: `Reste toi-même, c'est parfait ! 💯` },
    { greeting: `${firstName} !`, subtext: `T'es la définition de ${genial} ! 🌟` },
    
    // 😊 Messages simples et sincères
    { greeting: `Bonjour ${firstName} !`, subtext: `Sincèrement, ça fait plaisir de te voir ! 😊` },
    { greeting: `Salut ${firstName} !`, subtext: `Comment tu vas aujourd'hui ? On espère bien ! 🤗` },
    { greeting: `Coucou ${firstName} !`, subtext: `T'es toujours le/la bienvenu${e} ici ! 🏠` },
    { greeting: `Hey ${firstName} !`, subtext: `On pense à toi, tu sais ! 💭💜` },
    { greeting: `${firstName} !`, subtext: `Merci d'être ${firstName} ! 🙏` },
    { greeting: `Hello ${firstName} !`, subtext: `T'es quelqu'un de bien ! ⭐` },
    { greeting: `${firstName} !`, subtext: `On est ${content}${e}s de t'avoir ! 🤝` },
    { greeting: `Re ${firstName} !`, subtext: `Toujours fidèle au poste ! 💪` },
  ];

  // Sélection aléatoire
  const randomIndex = Math.floor(Math.random() * messages.length);
  let message = messages[randomIndex];
  
  // Correction des accords manqués
  message.subtext = message.subtext
    .replace(/le\/la/g, isFemale ? 'la' : 'le')
    .replace(/un\/une/g, isFemale ? 'une' : 'un')
    .replace(/King\/Queen/g, isFemale ? 'Queen' : 'King');
  
  message.greeting = message.greeting
    .replace(/le\/la/g, isFemale ? 'la' : 'le');

  return message;
}

/**
 * Génère un message basé sur l'heure de la journée
 */
export function getTimeBasedGreeting(firstName) {
  const hour = new Date().getHours();
  const gender = detectGender(firstName);
  const e = gender === 'f' ? 'e' : '';
  
  if (hour >= 5 && hour < 12) {
    return { greeting: `Bon matin ${firstName} !`, subtext: `Bien réveillé${e} ? C'est parti ! ☀️` };
  } else if (hour >= 12 && hour < 14) {
    return { greeting: `Bon appétit ${firstName} !`, subtext: `L'heure de se régaler ! 🍽️` };
  } else if (hour >= 14 && hour < 18) {
    return { greeting: `Bel après-midi ${firstName} !`, subtext: `On profite bien ? 😎` };
  } else if (hour >= 18 && hour < 22) {
    return { greeting: `Bonne soirée ${firstName} !`, subtext: `C'est l'heure de décompresser ! 🌙` };
  } else {
    return { greeting: `${firstName} le/la noctambule !`, subtext: `Toi aussi tu dors jamais ? 🦉` };
  }
}

export default getPositiveMessage;
